import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { apiGet } from '../lib/api';
import { useAuthStore } from '../stores/auth.store';
import type { DailyAnalyticsDetailDto, DailyAnalyticsDto } from '@bisa/shared';
import { PageSpinner } from '../components/ui/Spinner';

interface TrendPoint extends DailyAnalyticsDto {
  dateLabel: string;
}

function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="mt-1 text-3xl font-bold text-gray-900">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-gray-400">{sub}</p>}
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <h2 className="text-base font-semibold text-gray-800 mb-4">{title}</h2>
      {children}
    </div>
  );
}

async function exportPdf(
  daily: DailyAnalyticsDetailDto,
  date: string,
  facilityName: string,
) {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const margin = 14;
  let y = margin;

  const line = (text: string, size = 11, bold = false) => {
    doc.setFontSize(size);
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.text(text, margin, y);
    y += size * 0.5 + 2;
  };

  const hline = () => {
    doc.setDrawColor(200);
    doc.line(margin, y, 210 - margin, y);
    y += 4;
  };

  line('BISA — Shift Report', 16, true);
  line(`Facility: ${facilityName}`, 11);
  line(`Date: ${date}`, 11);
  line(`Generated: ${new Date().toLocaleString()}`, 9);
  hline();

  line('Summary', 13, true);
  y += 1;
  const stats: [string, string][] = [
    ['Total Issued', String(daily.totalIssued)],
    ['Served', `${daily.totalServed} (${daily.totalIssued > 0 ? Math.round((daily.totalServed / daily.totalIssued) * 100) : 0}%)`],
    ['No-Shows', String(daily.totalNoShow)],
    ['Expired', String(daily.totalExpired)],
    ['Cancelled', String(daily.totalCancelled)],
    ['Avg Service Time', `${Math.round(daily.avgProcessingTimeSecs / 60)} min`],
    ['FIFO Deviation', `${daily.fifoDeviationPct.toFixed(1)}%`],
  ];
  for (const [label, value] of stats) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(label + ':', margin, y);
    doc.setFont('helvetica', 'normal');
    doc.text(value, margin + 55, y);
    y += 6;
  }
  y += 2;
  hline();

  if (daily.serviceBreakdown.length > 0) {
    line('Service Breakdown', 13, true);
    y += 1;
    const colX = [margin, margin + 70, margin + 115, margin + 150];
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    ['Service', 'Served', 'Avg Time (min)', ''].forEach((h, i) => doc.text(h, colX[i], y));
    y += 5;
    doc.setFont('helvetica', 'normal');
    for (const svc of daily.serviceBreakdown) {
      doc.text(svc.serviceName.slice(0, 32), colX[0], y);
      doc.text(String(svc.totalServed), colX[1], y);
      doc.text(String(Math.round(svc.avgProcessingTimeSecs / 60)), colX[2], y);
      y += 5;
      if (y > 270) { doc.addPage(); y = margin; }
    }
    y += 2;
    hline();
  }

  if (daily.hourlyBreakdown.length > 0) {
    line('Hourly Activity', 13, true);
    y += 1;
    const colX = [margin, margin + 30, margin + 60];
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    ['Hour', 'Issued', 'Served'].forEach((h, i) => doc.text(h, colX[i], y));
    y += 5;
    doc.setFont('helvetica', 'normal');
    for (const b of daily.hourlyBreakdown) {
      const label = `${String(b.hour).padStart(2, '0')}:00`;
      doc.text(label, colX[0], y);
      doc.text(String(b.issued), colX[1], y);
      doc.text(String(b.served), colX[2], y);
      y += 5;
      if (y > 270) { doc.addPage(); y = margin; }
    }
  }

  doc.save(`bisa-shift-report-${date}.pdf`);
}

export function ReportsPage() {
  const { staff } = useAuthStore();
  const facilityId = staff!.facilityId;
  const facilityName = staff?.facilityName ?? 'Facility';

  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [daily, setDaily] = useState<DailyAnalyticsDetailDto | null>(null);
  const [trend, setTrend] = useState<TrendPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    try {
      setLoading(true);
      setError(null);
      const [dailyData, trendData] = await Promise.all([
        apiGet<DailyAnalyticsDetailDto>(`/analytics/facilities/${facilityId}/daily?date=${date}`),
        apiGet<DailyAnalyticsDto[]>(`/analytics/facilities/${facilityId}/trends?days=7`),
      ]);
      setDaily(dailyData);
      setTrend(
        trendData.map((d) => ({
          ...d,
          dateLabel: new Date(d.date + 'T12:00:00Z').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' }),
        })),
      );
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [facilityId, date]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async () => {
    if (!daily) return;
    setExporting(true);
    try {
      await exportPdf(daily, date, facilityName);
    } finally {
      setExporting(false);
    }
  };

  const avgMins = daily ? Math.round(daily.avgProcessingTimeSecs / 60) : 0;
  const servedPct = daily && daily.totalIssued > 0 ? Math.round((daily.totalServed / daily.totalIssued) * 100) : 0;

  const hourlyData = (daily?.hourlyBreakdown ?? []).map(b => ({
    ...b,
    label: `${String(b.hour).padStart(2, '0')}:00`,
  }));

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-semibold text-gray-900">Reports</h1>
        <div className="flex items-center gap-3">
          <input
            type="date"
            value={date}
            max={today}
            onChange={(e) => setDate(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleExport}
            disabled={!daily || exporting}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exporting ? 'Exporting…' : 'Export PDF'}
          </button>
        </div>
      </div>

      {loading && <PageSpinner />}
      {error && <p className="text-red-600">{error}</p>}

      {!loading && !error && daily && (
        <div className="space-y-6">
          {/* Summary stats */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            <StatCard label="Total Issued" value={daily.totalIssued} />
            <StatCard label="Served" value={daily.totalServed} sub={`${servedPct}% completion`} />
            <StatCard label="No-Shows" value={daily.totalNoShow} />
            <StatCard label="Expired" value={daily.totalExpired} />
            <StatCard label="Avg. Service Time" value={`${avgMins} min`} />
          </div>

          {/* FIFO score */}
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <p className="text-sm font-medium text-gray-700 mb-1">FIFO Deviation</p>
            <div className="flex items-center gap-3">
              <div className="flex-1 rounded-full bg-gray-100 h-3 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    daily.fifoDeviationPct < 5
                      ? 'bg-green-500'
                      : daily.fifoDeviationPct < 15
                        ? 'bg-yellow-400'
                        : 'bg-red-500'
                  }`}
                  style={{ width: `${Math.min(daily.fifoDeviationPct, 100)}%` }}
                />
              </div>
              <span className="text-sm font-semibold text-gray-700 w-12 text-right">
                {daily.fifoDeviationPct.toFixed(1)}%
              </span>
            </div>
            <p className="mt-1 text-xs text-gray-400">Out-of-sequence serves / total served</p>
          </div>

          {/* Hourly throughput chart */}
          {hourlyData.length > 0 && (
            <SectionCard title="Hourly Throughput">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={hourlyData} margin={{ top: 0, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="issued" name="Issued" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="served" name="Served" fill="#22c55e" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </SectionCard>
          )}

          {/* Service breakdown */}
          {daily.serviceBreakdown.length > 0 && (
            <SectionCard title="Avg Processing Time by Service">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="pb-2 text-left font-medium text-gray-500">Service</th>
                      <th className="pb-2 text-right font-medium text-gray-500">Served</th>
                      <th className="pb-2 text-right font-medium text-gray-500">Avg Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {daily.serviceBreakdown
                      .slice()
                      .sort((a, b) => b.totalServed - a.totalServed)
                      .map((svc) => (
                        <tr key={svc.serviceCategoryId} className="border-b border-gray-50 last:border-0">
                          <td className="py-2 text-gray-800">{svc.serviceName}</td>
                          <td className="py-2 text-right text-gray-600">{svc.totalServed}</td>
                          <td className="py-2 text-right font-medium text-gray-800">
                            {Math.round(svc.avgProcessingTimeSecs / 60)} min
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          )}

          {/* 7-day trend chart */}
          <SectionCard title="7-Day Trend">
            {trend.length === 0 ? (
              <p className="text-sm text-gray-400">No trend data available.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={trend} margin={{ top: 0, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="dateLabel" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="totalIssued" name="Issued" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="totalServed" name="Served" fill="#22c55e" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="totalNoShow" name="No-show" fill="#ef4444" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </SectionCard>
        </div>
      )}
    </div>
  );
}
